/** Wire and outbox types (ARCHITECTURE 9.2, 9.4, 9.6). */

export type CommandStatus =
  | 'queued' | 'sending'                                             // client-side only
  | 'applied' | 'applied_with_exception' | 'rejected' | 'conflict'   // terminal
  | 'blocked' | 'transient' | 'error' | 'discarded'

/** What the server may answer for one command (api.submit_commands). `deferred` is never stored: the command was not attempted. */
export type ServerStatus = Exclude<CommandStatus, 'queued' | 'sending' | 'discarded'> | 'deferred'

export const TERMINAL: ReadonlySet<CommandStatus> = new Set(['applied', 'applied_with_exception', 'rejected', 'conflict', 'discarded'])
export const RESOLVED_OK: ReadonlySet<CommandStatus> = new Set(['applied', 'applied_with_exception'])
/** Re-send automatically (the server may succeed later). */
export const RETRYABLE: ReadonlySet<CommandStatus> = new Set(['queued', 'transient', 'blocked'])

export interface CommandWire {
  command_id: string
  name: string
  version: number
  org_id: string
  site_id: string
  aggregate_id?: string
  depends_on: string[]
  payload: Record<string, unknown>
  occurred_at: string
  known_seq: number
  known_epoch?: string
  clock_offset_ms: number
  actor_person_id?: string
  /** Single-use server nonce for online-only commands (api.begin_online_action); never stored with the outbox for long: it lives 10 minutes. */
  nonce?: string
  /** A command sent from a shared workstation names the open session of the unlocked person (api.kiosk_unlock); the server checks it is theirs, on this device, and open. */
  kiosk_unlock_id?: string
}

export interface CommandResult {
  command_id: string
  status: ServerStatus
  code?: string | null
  exceptions?: string[]
  result?: Record<string, unknown> | null
  duplicate?: boolean
}

export interface SubmitResponse {
  epoch: string
  server_time: string
  results: CommandResult[]
}

/** A row the server removed (M11: only the retention job removes rows, non-negotiable 3): the device deletes this id from its mirror. `sync_seq` is the club's
 *  counter when it went, so a row the device holds with a LATER stamp (created again since) is kept. */
export interface Tombstone { table: string; id: string; sync_seq: number }

export interface PullResponse {
  epoch: string
  server_time: string
  me?: string | null
  hi?: number
  scope_version?: number
  tables?: Record<string, Array<Record<string, unknown>>>
  command_results?: Array<{
    command_id: string; name: string; version: number; aggregate_id: string | null; status: CommandStatus
    error_code: string | null; exceptions: string[]; result: Record<string, unknown> | null
    applied_at: string | null; attempts: number; sync_seq: number; device_id: string
  }>
  /** M11: the rows of the caller's profile and sites removed in this window (cursor, hi], oldest first, paged like the rows; never on a first snapshot (cursor 0) */
  tombstones?: Tombstone[]
  has_more?: boolean
  next_cursor?: number
  epoch_changed?: boolean
  resnapshot_required?: boolean
}

export interface ContextSite { site_id: string; name: string; timezone: string; roles: string[] }
export interface ContextOrg {
  org_id: string; org_name: string; default_locale: string
  /** The person acting on this device; null on a shared workstation (a device principal), which then carries its own device id. */
  person_id: string | null
  kiosk_device_id?: string | null
  display_name: string; preferred_locale: string; sites: ContextSite[]
}
